/**
 * Context menu for a folder tree node.
 * Appears on right-click or kebab button click.
 * Handles: New Subfolder, Rename, Delete.
 */

import { useState, useRef, useEffect } from 'react';
import { FolderPlus, Pencil, Trash2, X, Loader2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogPanel } from '@headlessui/react';
import type { IFolderNode } from '../types';

interface FolderTreeNodeMenuProps {
    folder: IFolderNode;
    position: { x: number; y: number };
    onClose: () => void;
    onCreateSubfolder: (parentId: string | undefined, name: string) => Promise<void>;
    onRename:          (id: string, name: string) => Promise<void>;
    onDelete:          (id: string, strategy: 'move' | 'delete', targetFolderId?: string) => Promise<void>;
    /** Flat list of all folders (for "move tests to" selector on delete). */
    allFolders: Array<{ _id: string; name: string; path: string }>;
}

export function FolderTreeNodeMenu({
    folder,
    position,
    onClose,
    onCreateSubfolder,
    onRename,
    onDelete,
    allFolders,
}: FolderTreeNodeMenuProps) {
    const menuRef                             = useRef<HTMLDivElement>(null);
    const [action, setAction]                = useState<'idle' | 'create' | 'rename' | 'delete'>('idle');
    const [inputValue, setInputValue]        = useState('');
    const [deleteStrategy, setDeleteStrategy]= useState<'move' | 'delete'>('move');
    const [targetFolderId, setTargetFolderId]= useState('');
    const [loading, setLoading]              = useState(false);
    const [error, setError]                  = useState<string | null>(null);

    // Close on outside click
    useEffect(() => {
        function handler(e: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose();
            }
        }
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    const isRoot = !folder.parentId && folder.path === '/root';

    async function handleCreate() {
        if (!inputValue.trim()) return;
        setLoading(true);
        setError(null);
        try {
            await onCreateSubfolder(folder._id, inputValue.trim());
            onClose();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to create folder');
        } finally {
            setLoading(false);
        }
    }

    async function handleRename() {
        if (!inputValue.trim()) return;
        setLoading(true);
        setError(null);
        try {
            await onRename(folder._id, inputValue.trim());
            onClose();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to rename folder');
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete() {
        setLoading(true);
        setError(null);
        try {
            await onDelete(folder._id, deleteStrategy, deleteStrategy === 'move' ? targetFolderId : undefined);
            onClose();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to delete folder');
        } finally {
            setLoading(false);
        }
    }

    // Available folders to move tests to (exclude self and descendants)
    const moveTargets = allFolders.filter(
        (f) => f._id !== folder._id && !f.path.startsWith(folder.path + '/') && f.path !== folder.path,
    );

    if (action === 'idle') {
        return (
            <div
                ref={menuRef}
                data-testid="folder-node-menu"
                className="fixed z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-gh-border-dark rounded-xl shadow-lg py-1 min-w-[160px]"
                style={{ left: position.x, top: position.y }}
            >
                <button
                    data-testid="folder-menu-new-subfolder"
                    onClick={() => { setInputValue(''); setAction('create'); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                    <FolderPlus size={14} /> New Subfolder
                </button>
                {!isRoot && (
                    <>
                        <button
                            data-testid="folder-menu-rename"
                            onClick={() => { setInputValue(folder.name); setAction('rename'); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        >
                            <Pencil size={14} /> Rename
                        </button>
                        <div className="h-px bg-slate-100 dark:bg-slate-700 my-1" />
                        <button
                            data-testid="folder-menu-delete"
                            onClick={() => { setTargetFolderId(moveTargets[0]?._id ?? ''); setAction('delete'); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors"
                        >
                            <Trash2 size={14} /> Delete
                        </button>
                    </>
                )}
            </div>
        );
    }

    // Inline input for create / rename
    if (action === 'create' || action === 'rename') {
        return (
            <div
                ref={menuRef}
                className="fixed z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-gh-border-dark rounded-xl shadow-lg p-3 w-56"
                style={{ left: position.x, top: position.y }}
            >
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                    {action === 'create' ? 'New subfolder name' : 'Rename folder'}
                </p>
                <input
                    autoFocus
                    aria-label={action === 'create' ? 'New subfolder name' : 'Rename folder'}
                    data-testid="folder-menu-name-input"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter')  action === 'create' ? handleCreate() : handleRename();
                        if (e.key === 'Escape') onClose();
                    }}
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-gh-border-dark rounded-lg bg-white dark:bg-gh-bg-dark text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                    placeholder="Folder name"
                />
                {error && <p className="text-xs text-rose-600 mb-2">{error}</p>}
                <div className="flex gap-2">
                    <button
                        onClick={action === 'create' ? handleCreate : handleRename}
                        disabled={!inputValue.trim() || loading}
                        className="flex-1 px-2 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded-lg disabled:opacity-50 transition-colors"
                    >
                        {loading ? <Loader2 size={12} className="animate-spin mx-auto" /> : action === 'create' ? 'Create' : 'Rename'}
                    </button>
                    <button onClick={onClose} className="px-2 py-1.5 text-xs text-slate-500 hover:text-slate-700 rounded-lg">
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    // Delete confirmation
    return (
        <div
            ref={menuRef}
            className="fixed z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-gh-border-dark rounded-xl shadow-lg p-3 w-64"
            style={{ left: position.x, top: position.y }}
        >
            <div className="flex items-start gap-2 mb-3">
                <AlertTriangle size={15} className="text-rose-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-700 dark:text-slate-300">
                    Delete <strong>"{folder.name}"</strong>? Choose what to do with its test cases.
                </p>
            </div>

            <div className="flex flex-col gap-1.5 mb-3">
                <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
                    <input type="radio" checked={deleteStrategy === 'move'} onChange={() => setDeleteStrategy('move')} className="accent-blue-600" />
                    Move tests to another folder
                </label>
                {deleteStrategy === 'move' && (
                    <select
                        value={targetFolderId}
                        onChange={(e) => setTargetFolderId(e.target.value)}
                        className="ml-4 text-xs border border-slate-200 dark:border-gh-border-dark rounded-lg px-2 py-1 bg-white dark:bg-gh-bg-dark text-slate-700 dark:text-slate-200 focus:outline-none"
                    >
                        {moveTargets.length === 0 ? (
                            <option value="">No other folders</option>
                        ) : moveTargets.map((f) => (
                            <option key={f._id} value={f._id}>{f.path === '/root' ? 'Root' : f.name}</option>
                        ))}
                    </select>
                )}
                <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer">
                    <input type="radio" checked={deleteStrategy === 'delete'} onChange={() => setDeleteStrategy('delete')} className="accent-rose-600" />
                    Delete all tests in this folder
                </label>
            </div>

            {error && <p className="text-xs text-rose-600 mb-2">{error}</p>}

            <div className="flex gap-2">
                <button
                    data-testid="folder-menu-confirm-delete"
                    onClick={handleDelete}
                    disabled={loading || (deleteStrategy === 'move' && !targetFolderId)}
                    className="flex-1 px-2 py-1.5 text-xs font-semibold bg-rose-600 hover:bg-rose-500 text-white rounded-lg disabled:opacity-50 transition-colors"
                >
                    {loading ? <Loader2 size={12} className="animate-spin mx-auto" /> : 'Delete Folder'}
                </button>
                <button onClick={onClose} className="px-2 py-1.5 text-xs text-slate-500 hover:text-slate-700 rounded-lg">
                    Cancel
                </button>
            </div>
        </div>
    );
}
